import { useEffect, useState } from 'react'

interface UseDismissibleWidgetOptions {
  storageKey: string
  defaultVisible?: boolean
}

interface UseDismissibleWidgetReturn {
  isVisible: boolean
  dismiss: () => void
  show: () => void
  toggle: () => void
}

export function useDismissibleWidget({
  storageKey,
  defaultVisible = true
}: UseDismissibleWidgetOptions): UseDismissibleWidgetReturn {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    try {
      const isDismissed = localStorage.getItem(storageKey) === 'true'
      requestAnimationFrame(() => setIsVisible(defaultVisible && !isDismissed))
    } catch {
      // Fallback if localStorage is not available
      requestAnimationFrame(() => setIsVisible(defaultVisible))
    }
  }, [storageKey, defaultVisible])

  const dismiss = () => {
    setIsVisible(false)
    try {
      localStorage.setItem(storageKey, 'true')
    } catch {
      // Handle localStorage errors silently
      console.warn('Could not save widget dismissal state')
    }
  }

  const show = () => {
    setIsVisible(true)
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // Handle localStorage errors silently
      console.warn('Could not remove widget dismissal state')
    }
  }

  const toggle = () => {
    if (isVisible) {
      dismiss()
    } else {
      show()
    }
  }

  return {
    isVisible,
    dismiss,
    show,
    toggle
  }
}
